import {CompanySettlementEntity} from "@ots-frontend/ots-data";
import {ActionIcon, Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {CommentModal} from "../../comment/CommentModal/CommentModal";
import {useEntityCommentModal} from "../../comment/EntityCommentModal/useEntityCommentModal";
import {IconMessagePlus} from "@tabler/icons-react";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {EntityCommentList} from "../../comment/EntityCommentList/EntityCommentList";
import { DefaultText } from "../../text/DefaultText/DefaultText";
import {EntityDocumentTable} from "../../table/DocumentTable/EntityDocumentTable/EntityDocumentTable";

export type CompanySettlementDetailsProps = {
  settlement: CompanySettlementEntity
}

export const CompanySettlementDetails = (props: CompanySettlementDetailsProps) => {
  const {settlement} = props;

  const {t} = useTranslation(["translation", "common"]);

  const {
    opened,
    handlers,
    onSubmit: onCommentSubmit,
    isCreating: isCommentCreating
  } = useEntityCommentModal({
    entity: settlement
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("common.entity.settlement.profile.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            {settlement.name &&
              <Grid.Col span={12}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("common.entity.settlement.profile.field.name.label", "Name")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {settlement.name}
                </Text>
              </Grid.Col>
            }
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.settlement.profile.field.company.label", "Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={settlement.companyName}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.companysettlement.profile.field.method.label", "Method")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={settlement.method}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.companysettlement.profile.field.status.label", "Status")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDatabaseCode value={settlement.status}/>
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.companysettlement.profile.info.section.documents.label", "Documents")}
            </Title>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <EntityDocumentTable objectId={settlement.id}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("common.entity.companysettlement.profile.info.section.comments.label", "Comments")}</Title>
            <HasAccess permissions={["edit"]}>
              <ActionIcon color="green" onClick={handlers.open}>
                <IconMessagePlus size={16}/>
              </ActionIcon>
            </HasAccess>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <EntityCommentList objectId={settlement.id}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <CommentModal
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCommentSubmit}
        loading={isCommentCreating}/>
    </Grid>
  );
};
